import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useState } from 'react';

import { BuyTicketFormValues } from '@/components/Form/MultiStepForm/BuyTicketForm.type';
import { useMultiStepFormState } from '@/components/Form/MultiStepForm/MultiStepFormContext';
import Button from '@/components/UI/Button';
import Link from '@/components/UI/Link';
import { EventManagerQuestion } from '@/types/eventManagerQuestion';
import { EventTicketType } from '@/types/eventTicketType';
import { Format } from '@/utils/format';
import { EventTicketTypeHelper } from '@/utils/helpers/EventTicketTypeHelper';
import { useCurrentLocale } from '@/utils/locale/localeClient';
import Routes from '@/utils/routes';

import EventPageAnswerQuestion from './EventPageAnswerQuestion';

interface EventPageBuyTicketSectionContentProps {
  ticketCount: number;
  eventManagerQuestions: EventManagerQuestion[];
  placeId: string;
  userToken?: string;
  eventTicketTypes: EventTicketType[];
}

export default function EventPageBuyTicketSectionContent({
  ticketCount,
  eventManagerQuestions,
  placeId,
  userToken,
  eventTicketTypes,
}: EventPageBuyTicketSectionContentProps) {
  const { formData, a } = useMultiStepFormState<BuyTicketFormValues>();
  const locale = useCurrentLocale();

  const prices = useMemo(() => {
    if (!formData.eventTicketTypes) return 0;
    return EventTicketTypeHelper.getTotalPriceForSelectedEventTicketTypes(
      formData.eventTicketTypes,
      eventTicketTypes
    );
  }, [eventTicketTypes, formData.eventTicketTypes, a]);
  // States
  const [showQuestionSection, setShowQuestionSection] =
    useState<boolean>(false);
  const [hasUserAnswered, setHasUserAnswered] = useState<boolean>(
    eventManagerQuestions.length === 0
  );

  // Hooks
  const t = useTranslations('Pages.Event.EventPageBuyTicketSection');

  // Effects
  useEffect(() => {
    if (ticketCount === 0) setShowQuestionSection(false);
  }, [ticketCount]);

  return (
    <div className='w-full rounded-lg bg-primary-filled-default p-6'>
      {Object.entries(formData.eventTicketTypes).map((e, i) => {
        const eventTicketType = formData.eventTicketType.find(
          (et) => et.id === e?.[0] && e?.[1]?.quantity && e[1].quantity > 0
        );
        if (!eventTicketType) return null;
        return (
          <div className='mb-1 text-sm flex justify-between' key={i}>
            <p>{eventTicketType?.name}</p>
            <p className=''>{e[1].quantity}</p>
          </div>
        );
      })}

      {prices && (
        <div className='mb-3 flex justify-between'>
          <p>Total</p>
          {prices.discountPrice !== undefined &&
          prices.discountPrice !== null ? (
            <div className='flex space-x-4'>
              <p className='text-xl font-bold text-gray-300 line-through'>
                {Format.price(prices.price || 0, locale)}
              </p>
              <p className='text-xl font-extrabold'>
                {Format.price(prices.discountPrice || 0, locale)}
              </p>
            </div>
          ) : (
            <p className='text-xl font-extrabold'>
              {Format.price(prices.price || 0, locale)}
            </p>
          )}
        </div>
      )}
      {!hasUserAnswered && eventManagerQuestions.length > 0 ? (
        <>
          {!showQuestionSection ? (
            <Button
              id='buyEventTicket'
              onClick={() => setShowQuestionSection(true)}
              color='white'
              width='full'
              className='font-bold'
              padding='xl'
            >
              {t('reserveNow')}
            </Button>
          ) : (
            <EventPageAnswerQuestion
              eventManagerQuestions={eventManagerQuestions}
              onSubmit={() => setHasUserAnswered(true)}
              onDismiss={() => setShowQuestionSection(false)}
            />
          )}
        </>
      ) : (
        <>
          <Link
            id='buyEventTicket'
            href={Routes.PlaceEventTicket(placeId, !!userToken ? '2' : '1')}
            color='white'
            width='full'
            className='font-bold'
            padding='xl'
          >
            {t('reserveNow')}
          </Link>
        </>
      )}
    </div>
  );
}
